import React, { useEffect, useState, useCallback } from "react";
import Container from "react-bootstrap/Container";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/es";
import { AiOutlineHistory, AiOutlineRead, AiOutlineIdcard, AiOutlineLineChart } from "react-icons/ai";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useSchoolRole } from "../../hooks/useSchoolRole";
import { StudentActivityModal } from "../../components/protected/activity/StudentActivity";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const PER_PAGE = 8;
const ACTIVE_DAYS = 7; // "activo" = ingresó en los últimos 7 días

// ── Monograma (avatar con iniciales) ──
const MONO = [
  ["#eb2178", "#ff7bb0"], ["#632d43", "#a24d6f"], ["#2e7d6b", "#5bc0aa"],
  ["#3b5bdb", "#7d97ff"], ["#d9770a", "#ffb64d"], ["#7048e8", "#a487ff"],
];
const initials = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  return ((w[0][0] || "") + (w[1]?.[0] || w[0][1] || "")).toUpperCase();
};
const hashIdx = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % MONO.length;
};
const Monogram = ({ name }) => {
  const [a, b] = MONO[hashIdx(name || "")];
  return (
    <span className="rp-mono-av" style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}>
      {initials(name)}
    </span>
  );
};

const activityOf = (lastAccessTs) => {
  if (!lastAccessTs) return { key: "never", days: null };
  const days = Math.floor((Date.now() / 1000 - lastAccessTs) / 86400);
  return { key: days <= ACTIVE_DAYS ? "active" : "inactive", days };
};

const Pager = ({ t, page, total, perPage, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;
  return (
    <div className="rp-pager">
      <button type="button" className="rp-page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      <span className="rp-page-info">{t("pageWord")} {page} / {totalPages}</span>
      <button type="button" className="rp-page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
    </div>
  );
};

const RepresentativePanel = () => {
  const { t } = useTranslations();
  const { role, loading: roleLoading } = useSchoolRole();
  const isRep = role === "representante";
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  moment.locale("es");

  const authHeader = useCallback(async () => {
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
    return { Authorization: `Bearer ${idToken}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/school/my-students`, { headers: await authHeader() });
      const data = await res.json();
      setStudents(data.students || []);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (!roleLoading && isRep) load();
  }, [roleLoading, isRep, load]);

  // ── Palabras del alumno (Agregadas / Familia) ──
  const [wordsStu, setWordsStu] = useState(null);
  const [actStu, setActStu] = useState(null);
  const [wordsData, setWordsData] = useState(null);
  const [wordsTab, setWordsTab] = useState("added");
  const [wPage, setWPage] = useState(1);

  const openWords = async (s) => {
    setWordsStu(s); setWordsData("loading"); setWordsTab("added"); setWPage(1);
    try {
      const res = await fetch(`${API_BASE}/api/school/student-words?uid=${s.uid}`, { headers: await authHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      setWordsData(Array.isArray(data.words) ? data.words : []);
    } catch (e) {
      setWordsData([]);
      Swal.fire({ icon: "error", title: t("errorTitle") || "Error", text: e.message });
    }
  };
  const changeWordsTab = (tab) => { setWordsTab(tab); setWPage(1); };
  const showWordMedia = (w) => {
    // La imagen se precarga: si el archivo ya no existe en Storage (404), no se muestra
    // rota — abre el modal solo con el audio y los datos.
    const fire = (imageUrl) =>
      Swal.fire({
        title: w.spanishName || "",
        html: w.spanishSound ? `<audio src="${w.spanishSound}" controls type="audio/mp4"/>` : "",
        imageUrl: imageUrl || undefined,
        imageAlt: w.spanishName || "",
        confirmButtonColor: "#eb2178",
      });
    if (w.imagePath) {
      const img = new Image();
      img.onload = () => fire(w.imagePath);
      img.onerror = () => fire(undefined);
      img.src = w.imagePath;
    } else fire(undefined);
  };
  const filterWords = (arr, tab) =>
    (Array.isArray(arr) ? arr : []).filter((w) =>
      tab === "family"
        ? (w.type !== "" && w.type !== null && w.type !== undefined)
        : ((w.isCreated === 1 || w.isCreated || w.isCreated === "true") && !w.type)
    );

  const renderStatus = (lastAccessTs) => {
    const { key, days } = activityOf(lastAccessTs);
    if (key === "never") return <span className="rp-chip rp-chip-never">{t("stNever")}</span>;
    if (key === "active") return <span className="rp-chip rp-chip-active">{t("stActive")}</span>;
    return <span className="rp-chip rp-chip-inactive">{t("stInactive")} · {days}{t("daysShort")}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(students.length / PER_PAGE));
  const p = Math.min(page, totalPages);
  const pageItems = students.slice((p - 1) * PER_PAGE, p * PER_PAGE);
  const active = students.filter((s) => activityOf(s.lastAccessTs).key === "active").length;
  const conns = students.reduce((n, s) => n + (s.connCount || 0), 0);

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto rp-wrap">
          <div className="rp-head">
            <div className="rp-head-ic"><AiOutlineIdcard /></div>
            <div>
              <div className="rp-eyebrow">{t("repEyebrow")}</div>
              <h2 className="rp-title">{t("myStudents")}</h2>
            </div>
          </div>
          <p className="rp-desc">{t("representativePanelDesc")}</p>

          {roleLoading || loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: "var(--rp-plum)" }} /></div>
          ) : !isRep ? (
            <div className="rp-card text-center text-muted py-4">{t("notAuthorized")}</div>
          ) : students.length === 0 ? (
            <div className="rp-card"><div className="rp-empty"><div className="rp-empty-ic">🎒</div><div className="rp-empty-t">{t("noStudentsRep")}</div></div></div>
          ) : (
            <div className="rp-card">
              <div className="rp-summary">
                <div className="rp-mini"><span className="rp-mini-n">{students.length}</span><span className="rp-mini-l">{t("students")}</span></div>
                <div className="rp-mini"><span className="rp-mini-n">{active}</span><span className="rp-mini-l">{t("summaryActive")}</span></div>
                <div className="rp-mini"><span className="rp-mini-n">{conns}</span><span className="rp-mini-l">{t("summaryConns")}</span></div>
              </div>
              <div className="rp-tablewrap">
                <Table className="rp-table mb-0">
                  <thead><tr>
                    <th style={{ width: 30 }}>#</th>
                    <th>{t("studentName")}</th>
                    <th><AiOutlineHistory className="me-1" />{t("colLastAccess")}</th>
                    <th className="text-center">{t("colConnections")}</th>
                    <th className="text-end">{t("colStatus")}</th>
                    <th className="text-center">{t("wordsCol")}</th>
                  </tr></thead>
                  <tbody>
                    {pageItems.map((s, i) => (
                      <tr key={s.uid}>
                        <td className="rp-idx">{(p - 1) * PER_PAGE + i + 1}</td>
                        <td>
                          <div className="rp-person"><Monogram name={s.learnerName} />
                            <div>
                              <div className="rp-person-name">{s.learnerName || "—"}</div>
                              <div className="rp-sub">{[s.grade, s.section].filter(Boolean).join(" · ") || s.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="rp-access">{s.lastAccessTs ? moment.unix(s.lastAccessTs).fromNow() : <span className="rp-muted">{t("neverWord")}</span>}</td>
                        <td className="text-center rp-conn">{s.connCount || 0}</td>
                        <td className="text-end">{renderStatus(s.lastAccessTs)}</td>
                        <td className="text-center">
                          <button type="button" className="rp-words-btn me-1" title={t("actView")} onClick={() => setActStu(s)}>
                            <AiOutlineLineChart />
                          </button>
                          <button type="button" className="rp-words-btn" title={t("viewWords")} onClick={() => openWords(s)}>
                            <AiOutlineRead />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Pager t={t} page={p} total={students.length} perPage={PER_PAGE} onPage={setPage} />
            </div>
          )}
        </Container>
        <Footer logged={true} />
      </main>

      {/* Modal: palabras del alumno (Agregadas / Familia) */}
      <StudentActivityModal student={actStu} onHide={() => setActStu(null)} />

      <Modal show={!!wordsStu} onHide={() => setWordsStu(null)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <span className="rp-sec-ic"><AiOutlineRead /></span>
            {t("studentWordsTitle")} {wordsStu?.learnerName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="rp-wtabs">
            <button type="button" className={`rp-wtab ${wordsTab === "added" ? "on" : ""}`} onClick={() => changeWordsTab("added")}>{t("added")}</button>
            <button type="button" className={`rp-wtab ${wordsTab === "family" ? "on" : ""}`} onClick={() => changeWordsTab("family")}>{t("family")}</button>
          </div>
          {wordsData === "loading" ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" style={{ color: "var(--rp-plum)" }} /></div>
          ) : (() => {
            const all = filterWords(wordsData, wordsTab);
            const WPER = 6;
            const total = all.length;
            const wp = Math.min(wPage, Math.max(1, Math.ceil(total / WPER)));
            const items = all.slice((wp - 1) * WPER, wp * WPER);
            if (!total) return <div className="rp-hint">{t("noWords")}</div>;
            return (
              <>
                <div className="rp-tablewrap">
                  <Table className="rp-table mb-0">
                    <thead><tr>
                      <th style={{ width: 30 }}>#</th>
                      <th>{t("spanish")}</th>
                      <th>{t("syllables")}</th>
                      <th>{t("english")}</th>
                      {wordsTab !== "family" && <th>{t("topic")}</th>}
                      <th className="text-center">{t("details")}</th>
                    </tr></thead>
                    <tbody>
                      {items.map((w, i) => (
                        <tr key={i}>
                          <td className="rp-idx">{(wp - 1) * WPER + i + 1}</td>
                          <td className="rp-person-name">{w.spanishName}</td>
                          <td><span className="rp-syl">{w.spanishBreakUp}</span></td>
                          <td className="rp-sub" style={{ maxWidth: "none" }}>{w.englishName}</td>
                          {wordsTab !== "family" && <td className="rp-sub" style={{ maxWidth: "none" }}>{w.tema}</td>}
                          <td className="text-center"><button type="button" className="rp-ver-btn" onClick={() => showWordMedia(w)}>{t("view")}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <Pager t={t} page={wp} total={total} perPage={WPER} onPage={setWPage} />
              </>
            );
          })()}
        </Modal.Body>
      </Modal>

      <style>{`
        :root { --rp-plum:#632d43; --rp-pink:#eb2178; --rp-ink:#241f2b; --rp-muted:#8b8593; --rp-line:#eceaf0; }
        .rp-wrap { max-width: 980px; }
        .rp-head { display:flex; align-items:center; gap:14px; }
        .rp-head-ic { width:46px; height:46px; border-radius:14px; display:flex; align-items:center; justify-content:center;
          font-size:1.4rem; color:#fff; background:linear-gradient(135deg,var(--rp-plum),var(--rp-pink)); flex-shrink:0; box-shadow:0 8px 18px rgba(99,45,67,.25); }
        .rp-eyebrow { font-size:.72rem; text-transform:uppercase; letter-spacing:1.2px; color:var(--rp-muted); font-weight:700; }
        .rp-title { font-size:1.7rem; font-weight:800; color:var(--rp-ink); margin:0; letter-spacing:-.5px; }
        .rp-desc { color:var(--rp-muted); margin:10px 0 22px; font-size:.95rem; max-width:640px; }
        .rp-card { background:#fff; border:1px solid var(--rp-line); border-radius:18px; box-shadow:0 4px 24px rgba(36,31,43,.05); margin-bottom:14px; padding:6px 20px 18px; }
        .rp-summary { display:flex; gap:26px; padding:16px 4px 8px; }
        .rp-mini { text-align:center; display:flex; flex-direction:column; }
        .rp-mini-n { font-size:1.35rem; font-weight:800; color:var(--rp-plum); line-height:1; }
        .rp-mini-l { font-size:.66rem; text-transform:uppercase; letter-spacing:.5px; color:var(--rp-muted); font-weight:700; margin-top:4px; }
        .rp-tablewrap { overflow-x:auto; }
        .rp-table { width:100%; min-width:560px; border-collapse:separate; border-spacing:0; margin:0; }
        .rp-table thead th { font-size:.68rem; text-transform:uppercase; letter-spacing:.6px; color:var(--rp-muted); font-weight:800; border-bottom:2px solid var(--rp-line); padding:10px 10px; white-space:nowrap; }
        .rp-table tbody td { padding:11px 10px; border-bottom:1px solid #f4f2f6; vertical-align:middle; font-size:.9rem; color:var(--rp-ink); }
        .rp-table tbody tr:last-child td { border-bottom:none; }
        .rp-table tbody tr:hover td { background:#fcfafb; }
        .rp-idx { color:#c3bcc9; font-weight:700; font-size:.82rem; }
        .rp-person { display:flex; align-items:center; gap:11px; }
        .rp-person-name { font-weight:600; color:var(--rp-ink); }
        .rp-sub { color:var(--rp-muted); font-size:.78rem; margin-top:1px; max-width:220px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .rp-mono-av { width:34px; height:34px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; color:#fff; font-weight:800; font-size:.78rem; flex-shrink:0; }
        .rp-access { color:var(--rp-ink); font-size:.86rem; white-space:nowrap; }
        .rp-muted { color:var(--rp-muted); }
        .rp-conn { font-weight:800; color:var(--rp-plum); }
        .rp-chip { display:inline-block; padding:3px 11px; border-radius:999px; font-size:.74rem; font-weight:700; white-space:nowrap; }
        .rp-chip-active { background:#e7f7ee; color:#1b7a43; }
        .rp-chip-inactive { background:#fff6e5; color:#a86a00; }
        .rp-chip-never { background:#f3f2f5; color:#9a94a3; }
        .rp-hint { color:var(--rp-muted); font-size:.88rem; padding:14px 2px; }
        .rp-pager { display:flex; align-items:center; justify-content:center; gap:16px; margin-top:14px; }
        .rp-page-btn { width:34px; height:34px; border-radius:10px; border:1.5px solid var(--rp-line); background:#fff; font-size:1.2rem; color:var(--rp-plum); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; }
        .rp-page-btn:disabled { opacity:.35; cursor:default; }
        .rp-page-info { font-size:.84rem; color:var(--rp-muted); font-weight:700; }
        .rp-empty { text-align:center; padding:34px 20px; }
        .rp-empty-ic { font-size:2.2rem; margin-bottom:8px; }
        .rp-empty-t { font-weight:700; color:var(--rp-ink); }
        .rp-sec-ic { width:38px; height:38px; border-radius:11px; display:inline-flex; align-items:center; justify-content:center; font-size:1.1rem; background:#e7f6f2; color:#2e7d6b; flex-shrink:0; }
        .rp-words-btn { width:32px; height:32px; border-radius:9px; border:1px solid var(--rp-line); background:#fff; color:var(--rp-plum); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; font-size:1rem; transition:all .15s; }
        .rp-words-btn:hover { background:#f7f0fb; border-color:#e2d3ec; color:var(--rp-pink); }
        .rp-wtabs { display:flex; gap:8px; margin-bottom:14px; }
        .rp-wtab { border:1.5px solid var(--rp-line); background:#fff; color:var(--rp-muted); border-radius:999px; padding:6px 18px; font-size:.85rem; font-weight:700; cursor:pointer; transition:all .15s; }
        .rp-wtab.on { background:linear-gradient(135deg,var(--rp-plum),var(--rp-pink)); color:#fff; border-color:transparent; }
        .rp-syl { display:inline-block; padding:2px 10px; border-radius:999px; background:#f3f2f5; color:#6b6572; font-size:.8rem; font-weight:600; }
        .rp-ver-btn { border:none; background:#f4eef1; color:var(--rp-plum); border-radius:9px; padding:5px 14px; font-size:.82rem; font-weight:700; cursor:pointer; }
        .rp-ver-btn:hover { background:#ece3e8; }
        @media (max-width:576px){ .rp-title { font-size:1.4rem; } .rp-card { padding:4px 12px 14px; } .rp-summary { gap:18px; } }
      `}</style>
    </div>
  );
};

export default RepresentativePanel;
